/**
 * Detached flight and the relay tunnel (plan R6, §3 "one flight model"). A cruising, lane-riding or tunnelling ship
 * has no Rapier body; its attitude integrates kinematically from the player's (or AI's) control at a scaled rate.
 * Allocation-free.
 */
import type { Quat } from '../../core/math'
import type { TravelConfig } from '../config'

/**
 * Integrates body rates into `rot` (in place, then normalised). Control axes follow the flight model: +pitch is a
 * positive rotation about local +X, +yaw (nose right) and +roll (roll right) are negative rotations about +Y and +Z.
 * Each rate is control × max rate (rad/s) × `rateScale`; the step is the exact rotation for constant rates over `dt`.
 */
export function integrateDetached(
  rot: Quat,
  pitch: number,
  yaw: number,
  roll: number,
  maxRatePitch: number,
  maxRateYaw: number,
  maxRateRoll: number,
  rateScale: number,
  dt: number,
): void {
  const wx = pitch * maxRatePitch * rateScale
  const wy = -yaw * maxRateYaw * rateScale
  const wz = -roll * maxRateRoll * rateScale
  const w = Math.sqrt(wx * wx + wy * wy + wz * wz)
  const half = (w * dt) / 2
  if (half > 0) {
    // rot ← rot ⊗ exp(ω dt / 2), with ω in the body frame.
    const s = Math.sin(half) / w
    const dx = wx * s
    const dy = wy * s
    const dz = wz * s
    const dw = Math.cos(half)
    const qx = rot.x
    const qy = rot.y
    const qz = rot.z
    const qw = rot.w
    rot.x = qw * dx + qx * dw + qy * dz - qz * dy
    rot.y = qw * dy - qx * dz + qy * dw + qz * dx
    rot.z = qw * dz + qx * dy - qy * dx + qz * dw
    rot.w = qw * dw - qx * dx - qy * dy - qz * dz
  }
  const l = Math.sqrt(rot.x * rot.x + rot.y * rot.y + rot.z * rot.z + rot.w * rot.w)
  if (l > 0) {
    rot.x /= l
    rot.y /= l
    rot.z /= l
    rot.w /= l
  } else {
    rot.w = 1
  }
}

export type TunnelPhase = 'enter' | 'tunnel' | 'exit' | 'done'

/**
 * The relay transit's phase at `t` seconds since it started. `enterS`, then the tunnel for at least `tunnelMinS`,
 * ending as soon as the destination is `ready` after that, or at `tunnelMaxS` regardless (the caller logs the
 * overrun); then `exitS`. `t` is measured from the transit start, and `ready` must stay true once it is.
 * `readyAtS` is the transit time at which `ready` first became true; it times the exit phase, so the caller passes
 * it once ready (omitted, readiness is taken as happening at `t`, which reports 'exit' but never 'done').
 */
export function tunnelPhase(t: number, ready: boolean, cfg: TravelConfig, readyAtS = t): TunnelPhase {
  const c = cfg.transit
  if (t < c.enterS) return 'enter'
  const minEnd = c.enterS + c.tunnelMinS
  const maxEnd = c.enterS + c.tunnelMaxS
  // The tunnel ends at the first moment it is both past the minimum and ready (or at the maximum).
  const readyEnd = ready ? (readyAtS > minEnd ? readyAtS : minEnd) : Infinity
  const tunnelEnd = readyEnd < maxEnd ? readyEnd : maxEnd
  if (t < tunnelEnd) return 'tunnel'
  if (t < tunnelEnd + c.exitS) return 'exit'
  return 'done'
}
